<template>
    <b-modal
        id="modalAddAttachment"
        ref="modalAddAttachment"
        :lazy="true"
        title-tag="h2"
        title="Link Attachments"
    >
        <b-form>
            <b-form-group
                label="Letter: "
                class
                label-cols="3"
                label-for="letter"
            >
                <div class="p-2">
                    {{ letterTitle }}
                </div>
            </b-form-group>
            <b-form-group
                label="Attachments: "
                label-cols="3"
                label-for="attachment"
            >
                <v-select
                    id="attachment"
                    name="attachment"
                    ref="vselectAttachment"
                    placeholder="Select attachment (required)"
                    v-model="attachment"
                    class="link-attachment show-actions v-select-search"
                    :options="attachmentOptionsFiltered"
                    :class="{ 'border-danger': errors.has('attachment') }"
                    :filterable="false"
                    :searchable="true"
                    label="title"
                    clearable
                    multiple
                    @search="onSearch"
                    @open="onOpenAttachment"
                    @close="onCloseAttachment"
                >
                    <template v-slot:option="{ title }">
                        <b>{{ title }}</b>
                    </template>

                    <template v-slot:no-options="{ search }">
                        <div>
                            <span v-if="isOptionLoading">
                                Loading...
                            </span>
                            <span v-else-if="search === ''">
                                Type to search
                            </span>
                            <span v-else-if="!pagination.hasNext">
                                No result
                            </span>
                        </div>
                    </template>
                    <template v-slot:list-footer>
                        <li
                            v-show="
                                pagination.hasNext &&
                                attachmentOptionsFiltered.length > 0
                            "
                            ref="footerItem"
                            class="px-4"
                        >
                            Loading...
                        </li>
                    </template>
                </v-select>
            </b-form-group>
        </b-form>
        <div slot="modal-footer" class="w-100">
            <b-button
                v-if="!loading"
                @click="$refs.modalAddAttachment.hide('cancel')"
                class="float-left"
                variant="secondary"
                >Cancel</b-button
            >
            <div class="float-right">
                <img v-if="loading" src="assets/ajax-loader.gif" />
                <b-button
                    v-if="!loading"
                    @click="save"
                    class="float-left"
                    variant="primary"
                    >Save</b-button
                >
            </div>
        </div>
    </b-modal>
</template>

<script>
import vSelect from "vue-select";
import { differenceBy, debounce, concat } from "lodash";
import PatientLetter from "@/services/patient-letter";
export default {
    name: "ModalAddAttachment",
    components: { vSelect },
    data() {
        return {
            attachmentOptions: [],
            attachment: [],
            loading: false,
            attachmentObserver: null,
            pagination: { page: 1, search: null, hasNext: false },
            isOptionLoading: false,
            abortController: [],
            indexAxios: 0
        };
    },
    props: {
        patientLetter: {
            type: Object,
            required: true
        }
    },
    computed: {
        letterTitle() {
            return this.patientLetter.title;
        },
        attachmentOptionsFiltered() {
            return differenceBy(this.attachmentOptions, this.attachment, "id");
        }
    },
    mounted() {},
    methods: {
        open() {
            this.getAttachmentOptions(this.indexAxios);
            this.attachment = this.patientLetter.attachments;
            this.$refs.modalAddAttachment.show();
            this.attachmentObserver = new IntersectionObserver(
                this.infiniteScroll
            );
        },
        async onOpenAttachment() {
            if (this.pagination && this.pagination.hasNext) {
                await this.$nextTick();
                if (this.$refs.footerItem) {
                    this.attachmentObserver.observe(this.$refs.footerItem);
                }
            }
        },
        onCloseAttachment() {
            this.attachmentObserver.disconnect();
        },
        async infiniteScroll([{ isIntersecting, target }]) {
            if (isIntersecting && this.pagination.hasNext) {
                const ul = target.offsetParent;
                const scrollTop = target.offsetParent.scrollTop;
                await this.getAttachmentOptions(this.indexAxios);
                await this.$nextTick();
                ul.scrollTop = scrollTop;
            }
        },
        onSearch(search) {
            this.searchAttachment(search, this);
        },
        searchAttachment: debounce(function (search, self) {
            self.pagination.search = search;
            self.pagination.page = 1;
            self.pagination.hasNext = false;
            if (self.abortController.length > 0) {
                self.abortController[self.indexAxios].abort();
            }
            self.indexAxios++;
            self.getAttachmentOptions(self.indexAxios);
        }, 350),
        async getAttachmentOptions(indexAxios) {
            let self = this;
            let isFirstPage = false;
            if (self.pagination.page == 1) {
                isFirstPage = true;
                self.attachmentOptions = [];
                self.abortController[indexAxios] = new AbortController();
            }
            self.isOptionLoading = true;
            const res = await PatientLetter.getAttachmentOptions(
                self.patientLetter.patient_id,
                {
                    page: self.pagination.page,
                    where: self.pagination.search
                        ? `[title, like, ${this.pagination.search}]`
                        : null,
                    signal: self.abortController[indexAxios].signal
                }
            );

            if (
                res.message &&
                res.message.code == 200 &&
                res.resource &&
                self.indexAxios == indexAxios
            ) {
                self.attachmentOptions = self.attachmentOptions.concat(
                    res.resource.data
                );
                self.pagination.hasNext =
                    res.resource.last_page > self.pagination.page;
                self.pagination.page++;
                if (isFirstPage) {
                    self.onOpenAttachment();
                }
                self.isOptionLoading = false;
            }
        },
        async save() {
            let self = this;
            const params = {
                attachment_ids: this.attachment.map(attachment => attachment.id)
            };

            self.loading = true;
            PatientLetter.addAttachment(self.patientLetter.id, params)
                .then(res => {
                    if (
                        res.response.message &&
                        res.response.message.code == 200
                    ) {
                        self.$refs.modalAddAttachment.hide();
                        self.$emit("reload");
                        self._message("Attachment updated");
                    } else {
                        throw "error";
                    }
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to update attachment",
                        "danger"
                    );
                    console.error(error);
                })
                .finally(() => {
                    self.loading = false;
                });
        }
    }
};
</script>
<style>
.link-attachment .vs__selected {
    max-width: 330px !important;
    overflow-wrap: anywhere !important;
}
</style>
